import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllAppointment, getSingleDoctor, updateAppointmentStatus } from '../../Redux/AdminReducer/action';
import { useNavigate, useParams } from 'react-router-dom';
import CustomSelect from '../../Components/CommonComponents/CustomSelect';
import PaginationButtons from '../../Components/CommonComponents/PaginationButtons';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomSpinner from '../../Components/CommonComponents/CustomSpinner';
import CustomBreadcrumbs from '../../Components/CommonComponents/CustomBreadcrumbs';
import CustomInput from '../../Components/CommonComponents/CustomInput';
import RightWrapper from '../../Components/CommonComponents/ComponentWrapper';
import { RadianButtonGroup } from '../../Components/CommonComponents/ComponentWrapper';
import { PostIsShown } from '../../Redux/Store/API/Appointment';
import { toast } from 'react-toastify';
import exportToExcel from '../../UtilityHookFunctions/exportToExcel';

// Older appointments were saved before these fields existed.
const dash = (v) => (v === undefined || v === null || v === '' ? '—' : v);
const LOCATION_LABELS = { clinic: 'Clinic', outside: 'Outside' };

const EXPORT_COLUMNS = [
  { key: 'bookDate', label: 'Date', format: (v) => (v ? String(v).split('T')[0] : '') },
  { key: 'bookTimeSlot', label: 'Time', format: (v) => (v?.startTime ? `${v.startTime} - ${v.endTime || ''}` : '') },
  { key: 'patient.name', label: 'Patient', format: (v, row) => [v, row?.patient?.surname].filter(Boolean).join(' ') },
  { key: 'doctor.name', label: 'Doctor' },
  { key: 'status', label: 'Status' },
  { key: 'bookedByName', label: 'Booked By', format: (v) => dash(v) },
  { key: 'bookingLocation', label: 'Booking Location', format: (v) => dash(LOCATION_LABELS[v] || v) },
  { key: 'channel', label: 'Channel', format: (v) => dash(v) },
  { key: 'sourcePage', label: 'Source Page', format: (v) => dash(v) },
];


export default function AppointmentList() {
  // Redux selectors to access state
  const { getAllAppointmentProcessing, getAllAppointmentSuccess, getAllAppointmentFail, getAllAppointmentData } = useSelector((state) => state.AdminReducer);
  const dispatch = useDispatch();
  const [query, setQuery] = useState({ search: '', page: 1, limit: 10 });
  const debounceTimer = useRef(null);
  const navigate = useNavigate();
  const { listType } = useParams();
    const [selected, setSelected] = useState();


  // Function to fetch data when the query changes
  const fetchData = () => {
    dispatch(getAllAppointment(query, listType));
  };

  useEffect(() => {
    fetchData();
  }, [query]);

  return (
    <RightWrapper>
    <div className="mt-4 w-full">
  {/* Top bar */}
  <div className="flex justify-between flex-wrap items-center mb-3 gap-3">
    <CustomBreadcrumbs
      data={[
        { title: "Dashboard", url: "/dashboard" },
        { title: "Appointments" },
      ]}
    />

    <div className="flex items-center gap-2">
    <button
      type="button"
      onClick={() =>
        exportToExcel(getAllAppointmentData || [], 'appointments', EXPORT_COLUMNS, 'Appointments')
      }
      disabled={!Array.isArray(getAllAppointmentData) || getAllAppointmentData.length === 0}
      className="px-3 py-1.5 text-sm rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      Export to Excel
    </button>
    <div className="w-56">
      <CustomInput
        label=""
        name="search"
        type="text"
        value={query.patient}
        onChange={(e) =>
          setQuery({ ...query, patient: e.target.value, page: 1 })
        }
        placeholder="Search patient…"
      />
    </div>
    </div>
  </div>

  {!getAllAppointmentProcessing && Array.isArray(getAllAppointmentData) ? (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 border-b bg-gradient-to-r from-slate-50 to-white rounded-t-xl">
        <h3 className="text-sm font-semibold text-gray-800 tracking-wide">
          Appointments
        </h3>
      </div>

      {/* Table */}
      <div className="overflow-x-auto h-[55vh] px-2">
        <table className="min-w-full border-separate border-spacing-y-2">
          <thead className="sticky top-0 z-10">
            <tr>
              {["Date", "Time", "Patient", "Booked By", "Booking Location", "Channel", "Source Page", "Actions"].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {[...(getAllAppointmentData || [])]
              .sort(
                (a, b) =>
                  new Date(b?.bookDate || 0) - new Date(a?.bookDate || 0)
              )
              .map((item) => (
                <tr
                  key={item._id}
                  className="
                    group bg-white rounded-lg
                    shadow-[0_1px_3px_rgba(0,0,0,0.06)]
                    hover:shadow-md hover:-translate-y-[1px]
                    transition-all duration-200
                  "
                >
                  <td className="px-3 py-2 text-sm text-gray-700">
                    {item?.bookDate?.split("T")[0]}
                  </td>

                  <td className="px-3 py-2 text-sm text-gray-700">
                    {item?.bookTimeSlot?.startTime} –{" "}
                    {item?.bookTimeSlot?.endTime}
                  </td>

                  <td className="px-3 py-2 text-sm font-medium text-gray-800">
                    {item?.patient?.name}
                  </td>

                  <td className="px-3 py-2 text-sm text-gray-700">
                    {dash(item?.bookedByName)}
                  </td>

                  <td className="px-3 py-2 text-sm text-gray-700">
                    {dash(LOCATION_LABELS[item?.bookingLocation] || item?.bookingLocation)}
                  </td>

                  <td className="px-3 py-2 text-sm text-gray-700">
                    {dash(item?.channel)}
                  </td>

                  <td className="px-3 py-2 text-xs text-gray-500 max-w-[180px] truncate" title={item?.sourcePage || ''}>
                    {dash(item?.sourcePage)}
                  </td>

                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-2 items-center">
                      <button
                        onClick={() =>
                          dispatch(
                            updateAppointmentStatus(
                              item?._id,
                              "Cancelled"
                            )
                          )
                        }
                        className="
                          px-2.5 py-1 text-xs rounded-full
                          bg-red-50 text-red-600
                          hover:bg-red-100
                          transition
                        "
                      >
                        Cancel
                      </button>

                      {item?.IsOnline && (
                        <button
                          onClick={() =>
                            navigate(`/video-consultation/${item?._id}`)
                          }
                          className="
                            px-2.5 py-1 text-xs rounded-full
                            bg-violet-50 text-violet-600
                            hover:bg-violet-100
                            transition
                          "
                        >
                          Join Call
                        </button>
                      )}

                      {item?.patient?.id && (
                        <button
                          onClick={() =>
                            navigate(
                              `/prescription/add/${item?.patient?.id}`
                            )
                          }
                          className="
                            px-2.5 py-1 text-xs rounded-full
                            bg-indigo-50 text-indigo-600
                            hover:bg-indigo-100
                            transition
                          "
                        >
                          Prescription
                        </button>
                      )}

                      <button
                        onClick={() =>
                          navigate(
                            `/appointment/${item?._id}/${item?.doctor?.id}`
                          )
                        }
                        className="
                          px-2.5 py-1 text-xs rounded-full
                          bg-gray-100 text-gray-700
                          hover:bg-gray-200
                          transition
                        "
                      >
                        Reschedule
                      </button>

                      <RadianButtonGroup
                        options={[
                          { label: "CheckIn", value: "true" },
                          { label: "No Show", value: "false" },
                        ]}
                        value={String(item.IsShown)}
                        onChange={async (value) => {
                          const result = await dispatch(
                            PostIsShown({
                              appointmentId: item._id,
                              isShown: value,
                            })
                          );
                          if (PostIsShown.fulfilled.match(result)) {
                            setSelected(value);
                            fetchData();
                          } else {
                            toast.error("Could not update status");
                          }
                        }}
                      />

                      {/* <button
                        onClick={() => {
                          const link = `https://admin.adityahomoeopathicclinic.com/public/appointment/Update/${item?._id}/${item?.doctor?.id}`;
                          navigator.clipboard
                            .writeText(link)
                            .then(() =>
                              toast.success("Link copied")
                            )
                            .catch(() =>
                              toast.error("Copy failed")
                            );
                        }}
                        className="
                          px-2.5 py-1 text-xs rounded-full
                          bg-gray-100 text-gray-700
                          hover:bg-gray-200
                          transition
                        "
                      >
                        Copy Link
                      </button> */}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {getAllAppointmentData?.length === 0 && (
          <div className="text-center text-gray-400 py-10">
            No appointments found
          </div>
        )}
      </div>

      {/* Footer / Pagination */}
      <div className="flex justify-between items-center px-4 py-3 border-t bg-gray-50 rounded-b-xl">
        <CustomSelect
          options={[10, 25, 50, 75, 100]}
          value={query.limit}
          onChange={(e) =>
            setQuery({ ...query, limit: e.target.value })
          }
          placeholder="Rows"
        />

        <div className="flex gap-2">
          <button
            disabled={query.page === 1}
            onClick={() =>
              setQuery({ ...query, page: query.page - 1 })
            }
            className="
              px-3 py-1.5 text-sm rounded-full
              bg-white border border-gray-300
              disabled:opacity-50 disabled:cursor-not-allowed
              hover:bg-gray-100
            "
          >
            Prev
          </button>

          <button
            disabled={
              getAllAppointmentData?.length < query.limit
            }
            onClick={() =>
              setQuery({ ...query, page: query.page + 1 })
            }
            className="
              px-3 py-1.5 text-sm rounded-full
              bg-white border border-gray-300
              disabled:opacity-50 disabled:cursor-not-allowed
              hover:bg-gray-100
            "
          >
            Next
          </button>
        </div>
      </div>
    </div>
  ) : (
    <CustomSpinner />
  )}

  <ToastContainer />
</div>


    </RightWrapper>
  );
}
